import type { Responsive } from '#types/common'
import type { GridGapSizes } from '#types/sizes'

const breakpoints = ['sm', 'md', 'lg'] as const

/**
 * Sizes a product card's media, widening it for a card promoted to a `focused-product-espot` tile.
 *
 * A focused card spans `FOCUSED_PRODUCT_SPAN` columns and rows, so it paints across two cells plus
 * the gap between them. Sizing it like a single cell asks Cloudinary for half the pixels it needs
 * and gives the box the wrong shape, so the focused branch adds the grid gap to a doubled cell.
 *
 * A card featured at any breakpoint is treated as focused at all of them. `resolveFocusedProducts`
 * can drop the tile at a single breakpoint (hidden there, or past the end of that grid), but authors
 * configure focus uniformly, so one flag keeps the media, the box and the card's classes in step.
 *
 * Gaps come from `product.list.grid`, which both the PLP and the CMS product grid pass to their
 * `base-grid` - a future grid with its own gaps would need to pass them in.
 * @param focused - Getter for the placement's `focused` flags. Omit for a normal card, which
 * returns exactly the configured `mediaSize`.
 * @returns `isFocused` for the card as a whole, plus the media `width`, `height` and `aspectRatio`
 * per breakpoint - those stay responsive because `mediaSize` and the grid gaps are.
 * @category Composables
 */
export const useProductCardMedia = (focused?: () => Responsive<boolean> | undefined) => {
  const { base, product } = useAppConfig().components
  const { mediaSize } = product.card
  const { gaps } = product.list.grid
  const gapTokens: Partial<Record<GridGapSizes, string>> = base?.grid?.gap || {}

  // A gap is authored as a token into the grid's gap scale, and a brand may leave it unset.
  const gapPx = (token?: GridGapSizes | null) => remToPx(token ? gapTokens[token] : undefined)

  const width = normalizeResponsive(mediaSize.width)
  const height = normalizeResponsive(mediaSize.height)
  const gapX = normalizeResponsive(gaps.x) as Responsive<GridGapSizes | null>
  const gapY = normalizeResponsive(gaps.y) as Responsive<GridGapSizes | null>

  const isFocused = computed(() => Object.values(normalizeResponsive(focused?.() ?? false)).some(Boolean))

  const box = computed(() => breakpoints.reduce((acc, breakpoint) => ({
    ...acc,
    [breakpoint]: isFocused.value
      ? {
          width: FOCUSED_PRODUCT_SPAN * width[breakpoint] + gapPx(gapX[breakpoint]),
          height: FOCUSED_PRODUCT_SPAN * height[breakpoint] + gapPx(gapY[breakpoint])
        }
      : { width: width[breakpoint], height: height[breakpoint] }
  }), {} as Record<typeof breakpoints[number], { width: number, height: number }>))

  return {
    isFocused,
    width: computed(() => mapObject(box.value, ({ width: value }) => value)),
    height: computed(() => mapObject(box.value, ({ height: value }) => value)),
    aspectRatio: computed(() => mapObject(box.value, ({ width: w, height: h }) => w / h)),
    /**
     * Cloudinary transformations for a focused tile, or `undefined` for a normal card.
     *
     * The tile is a different shape than the card it replaces, so the asset is padded out to the
     * box with generative fill rather than cropped to it - a focused product is the one thing on
     * the page that should not lose its edges. Two decimals keeps the URL readable and stays well
     * under a device pixel of the real box, so the padding is never cropped back off by `cover`.
     * Spread over the brand's `mediaTransformations`, it replaces their crop and background (a second
     * crop mode such as Vans' `alignFill` would land in the same component) and keeps the rest.
     */
    transformations: computed(() => isFocused.value
      ? {
          aspectRatio: mapObject(box.value, ({ width: w, height: h }) => Math.round((w / h) * 100) / 100),
          align: 'center' as const,
          alignFill: undefined,
          background: 'gen_fill' as const,
          backgroundAuto: undefined
        }
      : undefined
    )
  }
}
